import PropTypes from 'prop-types';
import { DropdownMenu, Button, Checkbox, Flex, Text } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import styles from './CalendarView.module.css';

export default function UnifiedFilterDropdown({
  highlightItems,
  selectedHighlights,
  onHighlightChange,
  broadcastItems,
  selectedBroadcasts,
  onBroadcastChange,
}) {
  const { t } = useTranslation('campaigns');

  const handleHighlightItemClick = (e, value) => {
    e.preventDefault();
    if (selectedHighlights.includes(value)) {
      onHighlightChange(selectedHighlights.filter(v => v !== value));
    } else {
      onHighlightChange([...selectedHighlights, value]);
    }
  };

  const handleBroadcastItemClick = (e, value) => {
    e.preventDefault();
    if (selectedBroadcasts.includes(value)) {
      onBroadcastChange(selectedBroadcasts.filter(v => v !== value));
    } else {
      onBroadcastChange([...selectedBroadcasts, value]);
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button variant="outline" size="2">
          <i className="fa-regular fa-filter" />
          <Text size="2">{t('calendar.filter')}</Text>
          <i className="fa-solid fa-chevron-down" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className={styles.filterDropdownContent}>
        {/* Highlights Section */}
        <DropdownMenu.Label>
          <Flex align="center" gap="2">
            <i className="fa-regular fa-star" style={{ width: 14 }} />
            <Text size="2" weight="medium">
              {t('calendar.highlights')}
            </Text>
          </Flex>
        </DropdownMenu.Label>
        {highlightItems.map(({ value, label, color }) => (
          <DropdownMenu.Item
            key={`highlight-${value}`}
            onSelect={e => handleHighlightItemClick(e, value)}
          >
            <Flex align="center" gap="2" className={styles.nestedFilterItem}>
              <Checkbox checked={selectedHighlights.includes(value)} />
              <span
                className={styles.filterDot}
                style={{ background: color }}
              />
              <Text size="2">{label}</Text>
            </Flex>
          </DropdownMenu.Item>
        ))}

        {broadcastItems.length > 0 && (
          <>
            <DropdownMenu.Separator />

            {/* Broadcasts Section */}
            <DropdownMenu.Label>
              <Flex align="center" gap="2">
                <i
                  className="fa-regular fa-paper-plane"
                  style={{ width: 14 }}
                />
                <Text size="2" weight="medium">
                  {t('calendar.broadcasts')}
                </Text>
              </Flex>
            </DropdownMenu.Label>
            {broadcastItems.map(({ value, label, color }) => (
              <DropdownMenu.Item
                key={`broadcast-${value}`}
                onSelect={e => handleBroadcastItemClick(e, value)}
              >
                <Flex
                  align="center"
                  gap="2"
                  className={styles.nestedFilterItem}
                >
                  <Checkbox checked={selectedBroadcasts.includes(value)} />
                  <span
                    className={styles.filterDot}
                    style={{ background: color }}
                  />
                  <Text size="2">{label}</Text>
                </Flex>
              </DropdownMenu.Item>
            ))}
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

UnifiedFilterDropdown.propTypes = {
  highlightItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedHighlights: PropTypes.arrayOf(PropTypes.string).isRequired,
  onHighlightChange: PropTypes.func.isRequired,
  broadcastItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedBroadcasts: PropTypes.arrayOf(PropTypes.string).isRequired,
  onBroadcastChange: PropTypes.func.isRequired,
};
